import { TVaultAprReportData } from "@components/V2/Vault/Common/VaultAprReports";
import APR_REPORT_AGETH from "@data/APR_REPORT_AGETH.json";
import APR_REPORT_HGETH from "@data/APR_REPORT_HGETH.json";
import APR_REPORT_SBUSD from "@data/APR_REPORT_SBUSD.json";

/**
 * Supported vault token types for strategy reports
 */
export type TTokenType = "agETH" | "hgETH" | "sbUSD";

/**
 * Mapping of token types to their corresponding strategy report JSON data
 */
const STRATEGY_REPORTS_MAP: Record<TTokenType, TVaultAprReportData> = {
  agETH: APR_REPORT_AGETH as TVaultAprReportData,
  hgETH: APR_REPORT_HGETH as TVaultAprReportData,
  sbUSD: APR_REPORT_SBUSD as TVaultAprReportData,
};

/**
 * Get the latest avgAPY from strategy reports JSON files
 * Returns the avgAPY from the first (latest) week in groupedByMonth
 *
 * @param vaultType - The vault token type (agETH, hgETH, or sbUSD)
 * @returns The latest avgAPY as a number, or null if not available
 *
 * @example
 * getLatestAvgApyFromReports("agETH")
 * // Returns: 8.33 (or null if not available)
 */
export const getLatestAvgApyFromReports = (
  vaultType: TTokenType
): number | null => {
  const reportData = STRATEGY_REPORTS_MAP[vaultType];

  if (!reportData) {
    return null;
  }

  // Get the latest week key from groupedByMonth (first month, first week)
  const latestWeekKey = reportData.groupedByMonth?.[0]?.weeks?.[0]?.key;
  if (!latestWeekKey) {
    return null;
  }

  // Get the apy from the weekDataMap using the latest week key
  const weekData = reportData.weekDataMap[latestWeekKey];
  const avgApy = weekData?.apy;

  // Validate and convert to number
  if (avgApy && !isNaN(+avgApy)) {
    return +avgApy;
  }

  return null;
};
